<template>
  <SimplePage title="アラート一覧" :showBack="!isSP" :onHome="back" :storeSelectable="isSP">
    <template #main>
      <div class="main">
        <table class="closed">
          <tbody>
            <tr
              class="item alert"
              v-for="(empty, key) in alertListEmpty"
              :key="key"
              @click="!isSP && goDetail(empty.type, empty.rsvId, key)"
            >
              <td class="importance">
                <span class="label">{{ alertLabel(empty.type) }}</span>
              </td>
              <td class="message">
                <span>{{ empty.message }}</span>
                <p v-if="isSP" class="sp-closed-text">※ぐるなび台帳iPad版から設定をしてください。</p>
              </td>
            </tr>
          </tbody>
        </table>
        <table class="list">
          <thead class="sp-toggle">
            <tr>
              <th class="importance">重要度</th>
              <th class="time">時間</th>
              <th class="people">人数</th>
              <th class="name">予約者</th>
              <th class="table">テーブル</th>
              <th class="message">アラート内容</th>
            </tr>
          </thead>
          <tbody v-if="isNoData || loadInitial" slot="before" class="no-closed">
            <LoadingIndicator v-if="loadInitial" />
            <tr v-else class="no-data">
              <td>
                <BellIcon width="64" height="64" />
                <span>該当するアラートはありません</span>
              </td>
            </tr>
          </tbody>
          <DynamicScroller
            v-else
            :items="alerts"
            :min-item-size="56"
            v-infinite-scroll="fetch"
            infinite-scroll-disabled="infiniteDisabled"
            infinite-scroll-distance="800"
          >
            <template v-if="isActive" v-slot="{ item, index, active }">
              <DynamicScrollerItem :item="item" :active="active" :data-index="index" watchData emitResize>
                <tr v-if="isVisibleIndex(index)" class="reserve-date">
                  <td>{{ formatDate(item.index, 'YYYY/M/D (dd)') }}</td>
                </tr>
                <tr
                  :class="['item', item.isImportance === 1 && 'alert', item.type === 4 && 'contact']"
                  @click="goDetail(item.type, item.rsvId, item.index)"
                >
                  <td class="importance">
                    <span class="label">{{ alertLabel(item.type) }}</span>
                  </td>
                  <template v-if="item.type !== 3">
                    <td class="time">
                      <span v-if="item.rsvStartTime">{{ filterTime(item.rsvStartTime) }} 〜 </span>
                    </td>
                    <td class="people">
                      <span v-if="item.rsvNum">{{ item.rsvNum }}</span>
                    </td>
                    <td class="name">
                      {{ isBlockSyncType(item.syncType) ? 'ブロック' : item.rsvName }}
                      <span class="suffix" v-if="item.rsvName">様</span>
                    </td>
                    <td class="table">
                      <span v-if="item.tableName">{{ item.tableName }}</span>
                    </td>
                  </template>
                  <td class="message">
                    <span>{{ item.message }}</span>
                    <div class="sp-message">
                      <p v-if="isBlockSyncType(item.syncType)">ブロック</p>
                      <p v-else-if="item.rsvName">{{ item.rsvName }}<span class="sp-rsv-name-suffix"> 様</span></p>
                      <div v-if="item.rsvStartTime" class="sp-message-grid">
                        <ClockIcon width="16" height="16" />
                        <span>{{ filterTime(item.rsvStartTime) }} 〜</span>
                      </div>
                      <div v-if="item.rsvNum" class="sp-message-grid">
                        <addPeopleIcon width="16" height="16" />
                        <span> {{ item.rsvNum }}名 </span>
                      </div>
                      <div v-if="item.tableName" class="sp-message-grid">
                        <TableIcon width="16" height="16" />
                        <span class="sp-tables-label">{{ item.tableName }}</span>
                        <span v-if="tableCount(item.tableName) >= 3" class="sp-table-count">
                          全{{ tableCount(item.tableName) }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </DynamicScrollerItem>
            </template>
            <tr v-if="busy" slot="after">
              <LoadingIndicator small />
            </tr>
          </DynamicScroller>
        </table>
      </div>
    </template>
  </SimplePage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import InfiniteScroll from 'vue-infinite-scroll'
import { formatDate } from '@/utils/date'
import { formatTime } from '@/utils/time'
import { isResrakuAlignment } from '@/utils/store'
import { isBlockSyncType } from '@/resources/reservation/syncTypeMap'
import SimplePage from '@/components/templates/SimplePage'
import { DynamicScroller, DynamicScrollerItem } from '@/components/molecules/VirtualScroller'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import BellIcon from '@/components/icons/bell'
import ClockIcon from '@/components/icons/clock'
import AddPeopleIcon from '@/components/icons/addPeople'
import TableIcon from '@/components/icons/table'

export default {
  name: 'Alert',
  components: {
    SimplePage,
    DynamicScroller,
    DynamicScrollerItem,
    LoadingIndicator,
    BellIcon,
    ClockIcon,
    AddPeopleIcon,
    TableIcon,
  },
  directives: {
    InfiniteScroll,
  },
  props: {
    backTo: String,
  },
  computed: {
    ...mapState({
      currentStore: state => state.stores.current.store,
      alertListEmpty: state => state.alerts.alertList.empty || [],
      alertListDate: state => state.alerts.alertList.date || [],
      busy: state => state.alerts.busy,
      ended: state => state.alerts.ended,
      isSP: state => state.app.isSP,
    }),
    ...mapGetters({
      alertLabel: 'alert/labels/get',
    }),
    alerts() {
      return this.alertListDate.map((o, i) => ({ ...o, id: i }))
    },
    isActive() {
      return this.alertListDate.length > 0
    },
    isNoData() {
      return !this.alertListEmpty.length && !this.alertListDate.length && this.ended
    },
    infiniteDisabled() {
      return this.busy || this.ended || !this.alertListDate.length
    },
    loadInitial() {
      return this.busy && !this.alertListEmpty.length && !this.alertListDate.length && !this.ended
    },
    isResrakuAlignment() {
      return isResrakuAlignment(this.currentStore)
    },
  },
  async mounted() {
    await this.$store.commit('alerts/clear')
    this.fetch()
  },
  methods: {
    formatDate,
    isBlockSyncType,
    filterTime(time) {
      if (!time || time.indexOf(':') > -1) return ''
      return formatTime(`${time.slice(0, 2)}:${time.slice(-2)}`)
    },
    fetch() {
      this.$store.dispatch('alerts/get')
    },
    async goDetail(type, rsvId = null, date = null) {
      if (this.isSP) {
        return this.$router.push(
          rsvId
            ? { name: 'ReserveDetail', params: { id: rsvId }, query: { alert: type } }
            : { name: 'ReserveList', query: { date } },
        )
      }

      const { reservesView } = this.currentStore
      if (type === 0) {
        return location.assign('/businessday/init')
      } else if (type === 3 && date) {
        return location.assign(`/reservation/${reservesView}?date=${date.replace(/-/g, '')}`)
      } else if ([10, 11, 12, 13, 17, 21, 22, 26, 39].includes(type)) {
        return location.assign(`/reservation/history/detail?reservation_id=${rsvId}&alert_type=${type}`)
      } else if (type === 27) {
        if (!this.isResrakuAlignment) {
          await this.$store.dispatch('alerts/delete', { rsvId, type })
        }
        return location.assign(`/resraku`)
      } else if (type === 28) {
        await this.$store.dispatch('alerts/delete', { rsvId, type })
        return location.assign(`/home/init/?alert_type=${type}`)
      } else if (rsvId) {
        if (reservesView === 'layout') {
          return location.assign(`/reservation/${reservesView}/reserve/${rsvId}?alert_type=${type}`)
        }
        return location.assign(`/reservation/${reservesView}?rsv_id=${rsvId}&alert_type=${type}`)
      }
      return location.assign('/home/init')
    },
    isVisibleIndex(index) {
      return index === 0 || this.alertListDate[index].index !== this.alertListDate[index - 1].index
    },
    back() {
      if (this.isSP) {
        this.$router.back()
      } else {
        location.href = this.backTo
      }
    },
    tableCount(table) {
      return table.split('、').length
    },
  },
}
</script>

<style lang="postcss" scoped>
.main {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
}

table {
  width: 100%;

  tr {
    display: flex;
    width: 100%;

    & + tr {
      border-top: none;
    }
  }

  thead {
    tr {
      border-top: none;
    }
  }

  .no-closed {
    align-items: center;
    display: flex;
    height: 100%;
  }

  tr:not(.reserve-date, .no-data) {
    border-bottom: 1px solid var(--border-color);
  }

  th,
  td {
    box-sizing: border-box;
  }

  th {
    background-color: var(--very-dark-gray);
    color: var(--white);
    font-size: 13px;
    height: 32px;
    line-height: 32px;
    padding: 0 16px;
    text-align: center;

    & + th {
      border-left: 1px solid var(--white);
    }
  }

  td {
    align-items: center;
    box-sizing: border-box;
    color: var(--text-color);
    display: flex;
    min-height: 56px;
    padding: 0 16px;
    position: relative;
    text-decoration: none;

    & + td {
      border-left: dashed 1px var(--border-color);
    }
  }
}

.list {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
}

.item {
  background-color: var(--white);
  cursor: pointer;
}

.importance {
  min-width: 88px;
}

.time {
  min-width: 112px;
}

.people {
  justify-content: center;
  min-width: 72px;

  span::after {
    content: '名';
    font-size: 13px;
  }
}

.name {
  flex-basis: 22%;
  min-width: 168px;

  .suffix {
    font-size: 13px;
    padding-left: 8px;
  }
}

.table {
  flex-basis: 30%;
  min-width: 224px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.message {
  flex: 1.5 1 48%;
}

.label {
  border: 1px solid var(--very-dark-gray);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  margin: auto;
  text-align: center;
  width: 56px;
}

.alert {
  .label {
    background-color: var(--pure-red);
    border: none;
    color: var(--white);
  }

  .message {
    color: var(--pure-red);
    font-weight: 600;
  }
}

.reserve-date {
  background-color: var(--very-light-gray-2);
  border-bottom: none;
  font-weight: bold;
  line-height: 2.1;

  & + .item {
    border-top: none;
  }

  td {
    min-height: 0;
  }
}

.contact {
  background-color: var(--very-pale-red);
}

.no-data > td {
  background-color: transparent;
  fill: var(--light-gray);
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  span {
    margin-top: 8px;
  }
}

.sp-message {
  display: none;
}

.responsive {
  @media screen and (max-width: 890px) {
    .sp-toggle,
    .time,
    .people,
    .name,
    .table {
      display: none;
    }

    table td {
      display: table-cell;
    }

    .list {
      height: 100%;
      overflow: unset;
    }

    .importance,
    .message {
      padding: 20px 16px 16px;
    }

    .label {
      box-sizing: border-box;
      display: block;
      height: 24px;
      padding-top: 5px;
      width: 64px;
    }

    .reserve-date {
      box-sizing: border-box;
      display: flex;
      font-size: 13px;
      height: 32px;
      padding: 2px 0;
      width: 100%;
    }

    .no-data > td {
      text-align: center;
      span {
        display: block;
      }
    }

    .sp-message {
      color: var(--text-color);
      display: block;
      font-weight: normal;
      line-height: 1.7;
      margin-top: 8px;
    }

    .sp-message-grid {
      align-items: center;
      display: grid;
      grid-auto-flow: column;
      grid-gap: 0 8px;
      grid-template-columns: 16px 1fr;

      svg {
        fill: var(--very-dark-gray-2);
      }
    }

    .sp-tables-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .sp-table-count {
      border: 1px solid var(--dark-gray-1);
      box-sizing: border-box;
      color: var(--very-dark-gray-2);
      font-size: 10px;
      height: 16px;
      line-height: 1.4;
      padding: 0 4px;
    }
  }

  .closed {
    .item {
      background: var(--very-light-gray-3);
      cursor: default;
    }

    .message {
      display: table-cell;
      padding: 20px 16px 16px;
    }
  }

  .sp-closed-text {
    color: var(--text-color);
    font-size: 13px;
  }

  .sp-rsv-name-suffix {
    font-size: 13px;
  }
}
</style>
